import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    FaArrowRight,
    FaSearch,
    FaSort,
    FaSortDown,
    FaSortUp,
    FaSync,
    FaMagic,
    FaTimes,
} from 'react-icons/fa';
import QuizOptionsModal from './QuizOptionsModal';
import PointsConfirmationModal from './PointsConfirmationModal';
import { regenerateQuiz } from './FileUploader';
import { QUIZMASTER_API } from '../config';
import { formatToLocalDateTime, getUtcTimestamp } from '../utils/date';
import '../styles/styles.css';
import FloatingChat from './FloatingChat';

const QuizList = ({ accessToken, supabaseRefreshToken, onSignIn }) => {
    const [quizData, setQuizData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [apiStatus, setApiStatus] = useState('');
    const [isRegenerating, setIsRegenerating] = useState(false);
    const [regenerateModalVisible, setRegenerateModalVisible] = useState(false);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'updated_at', direction: 'desc' });
    const location = useLocation();
    const navigate = useNavigate();
    const document = location.state?.selectedDocument;
    const regeneratePromiseRef = useRef(null);
    const costConfirmationResolveRef = useRef(null);
    const [costConfirmation, setCostConfirmation] = useState(null);

    const startTimer = useCallback(() => null, []);

    const stopTimer = useCallback(() => {}, []);

    const handleRegenerationLoading = useCallback((value) => {
        setIsRegenerating(Boolean(value));
    }, []);

    useEffect(() => {
        return () => {
            if (costConfirmationResolveRef.current) {
                costConfirmationResolveRef.current(false);
                costConfirmationResolveRef.current = null;
            }
        };
    }, []);

    const fetchQuizzesAndInstances = useCallback(async ({ showSpinner = false } = {}) => {
        if (!document) {
            setError('No document selected.');
            if (showSpinner) {
                setLoading(false);
            } else {
                setRefreshing(false);
            }
            return;
        }

        setError(null);
        if (showSpinner) {
            setLoading(true);
        } else {
            setRefreshing(true);
        }

        let authToken = accessToken;

        try {
            const quizUrl = `${QUIZMASTER_API}/get-quizzes-by-document/?document_id=${document.id}`;
            let quizResponse = await fetch(quizUrl, {
                headers: { 'Accept': 'application/json', 'Authorization': `Bearer ${authToken}` },
            });

            if (!quizResponse.ok) {
                if (quizResponse.status === 401 && supabaseRefreshToken) {
                    const refreshResponse = await fetch(`${QUIZMASTER_API}/auth/refresh`, {
                        method: 'POST',
                        headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
                        body: JSON.stringify({ refresh_token: supabaseRefreshToken }),
                    });
                    if (!refreshResponse.ok) {
                        throw new Error('Token refresh failed');
                    }
                    const { access_token: newAccessToken, refresh_token: newRefreshToken } = await refreshResponse.json();
                    onSignIn({ user_id: document.user_id, accessToken: newAccessToken, refreshToken: newRefreshToken });
                    authToken = newAccessToken;
                    quizResponse = await fetch(quizUrl, {
                        headers: { 'Accept': 'application/json', 'Authorization': `Bearer ${authToken}` },
                    });
                    if (!quizResponse.ok) {
                        throw new Error('Failed to fetch quizzes after token refresh');
                    }
                } else {
                    throw new Error('Failed to fetch quizzes');
                }
            }

            const quizzesData = await quizResponse.json();
            const quizzes = quizzesData.quizzes || [];

            if (quizzes.length === 0) {
                setQuizData([]);
                return;
            }

            const quizzesWithInstances = await Promise.all(
                quizzes.map(async (quiz) => {
                    const instanceResponse = await fetch(
                        `${QUIZMASTER_API}/get-quiz-instances-by-quiz/?quiz_id=${quiz.id}`,
                        { headers: { 'Accept': 'application/json', 'Authorization': `Bearer ${authToken}` } }
                    );
                    if (!instanceResponse.ok) {
                        console.warn(`Failed to fetch instances for quiz ${quiz.id}`);
                        return { ...quiz, instances: [] };
                    }
                    const instanceData = await instanceResponse.json();
                    const instancesWithScores = await Promise.all(
                        instanceData.quiz_instances.map(async (instance) => {
                            try {
                                const scoreResponse = await fetch(
                                    `${QUIZMASTER_API}/get-quiz-instance-results/?instance_id=${instance.instance_id}`,
                                    { headers: { 'Accept': 'application/json', 'Authorization': `Bearer ${authToken}` } }
                                );
                                if (!scoreResponse.ok) {
                                    return { ...instance, score_percentage: null };
                                }
                                const scoreData = await scoreResponse.json();
                                return { ...instance, score_percentage: scoreData.score_percentage };
                            } catch (err) {
                                console.warn(`Failed to fetch score for instance ${instance.instance_id}`);
                                return { ...instance, score_percentage: null };
                            }
                        })
                    );
                    return { ...quiz, instances: instancesWithScores };
                })
            );
            setQuizData(quizzesWithInstances);
        } catch (err) {
            console.error('[QuizList] Error fetching quizzes:', err);
            setError(err.message);
        } finally {
            if (showSpinner) {
                setLoading(false);
            } else {
                setRefreshing(false);
            }
        }
    }, [document, accessToken, supabaseRefreshToken, onSignIn]);

    useEffect(() => {
        fetchQuizzesAndInstances({ showSpinner: true });
    }, [fetchQuizzesAndInstances]);

    const refreshAfterRegen = useCallback(() => {
        fetchQuizzesAndInstances({ showSpinner: false });
    }, [fetchQuizzesAndInstances]);

    const quizInstances = useMemo(() => {
        if (!Array.isArray(quizData)) {
            return [];
        }

        return quizData.flatMap((quiz) => {
            if (!quiz || !Array.isArray(quiz.instances) || quiz.instances.length === 0) {
                return [];
            }

            const quizTitle = quiz.name || quiz.title || quiz.quiz_type || 'Quiz';
            const baseCreatedAt = quiz.created_at || null;
            const baseUpdatedAt = quiz.updated_at || baseCreatedAt;

            return quiz.instances.map((instance) => {
                const instanceCreatedAt = instance?.created_at || baseCreatedAt;
                const instanceUpdatedAt = instance?.updated_at || instanceCreatedAt || baseUpdatedAt;
                const rawScore = instance?.score_percentage;
                const numericScore = rawScore === null || rawScore === undefined || rawScore === ''
                    ? null
                    : Number(rawScore);

                return {
                    quizId: quiz.id,
                    quizType: quiz.quiz_type || quizTitle,
                    quizTitle,
                    instance_id: instance?.instance_id,
                    status: instance?.status || 'Unknown',
                    score_percentage: Number.isNaN(numericScore) ? null : numericScore,
                    created_at: instanceCreatedAt,
                    updated_at: instanceUpdatedAt,
                    rawInstance: instance,
                };
            });
        });
    }, [quizData]);

    const filteredInstances = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        if (!term) {
            return quizInstances;
        }

        return quizInstances.filter((instance) => {
            const tokens = [
                instance.quizTitle,
                instance.quizType,
                instance.instance_id,
                instance.status,
                instance.score_percentage !== null && instance.score_percentage !== undefined
                    ? `${instance.score_percentage}`
                    : '',
            ];

            return tokens.some((value) =>
                value && String(value).toLowerCase().includes(term)
            );
        });
    }, [quizInstances, searchTerm]);

    const sortedInstances = useMemo(() => {
        const { key, direction } = sortConfig;
        if (!key) {
            return filteredInstances;
        }

        const multiplier = direction === 'asc' ? 1 : -1;

        const normalizeScore = (score, dir) => {
            if (score === null || score === undefined || Number.isNaN(Number(score))) {
                return dir === 'asc' ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY;
            }
            return Number(score);
        };

        return [...filteredInstances].sort((a, b) => {
            switch (key) {
                case 'status':
                    return multiplier * String(a.status || '').localeCompare(String(b.status || ''));
                case 'score': {
                    const aScore = normalizeScore(a.score_percentage, direction);
                    const bScore = normalizeScore(b.score_percentage, direction);
                    return multiplier * (aScore - bScore);
                }
                case 'quiz_type':
                    return multiplier * String(a.quizTitle || '').localeCompare(String(b.quizTitle || ''));
                case 'updated_at':
                default: {
                    const aDate = getUtcTimestamp(a.updated_at ?? a.created_at);
                    const bDate = getUtcTimestamp(b.updated_at ?? b.created_at);
                    return multiplier * ((aDate ?? 0) - (bDate ?? 0));
                }
            }
        });
    }, [filteredInstances, sortConfig]);

    const handleSort = useCallback((key) => {
        setSortConfig((prev) => {
            if (prev.key === key) {
                return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
            }

            const defaultDirection = key === 'updated_at' || key === 'score' ? 'desc' : 'asc';
            return { key, direction: defaultDirection };
        });
    }, []);

    const getSortIcon = useCallback((key) => {
        if (sortConfig.key !== key) {
            return <FaSort size={12} />;
        }
        return sortConfig.direction === 'asc' ? <FaSortUp size={12} /> : <FaSortDown size={12} />;
    }, [sortConfig]);

    const formatDate = useCallback(
        (dateStr) =>
            formatToLocalDateTime(dateStr, {
                fallback: 'N/A',
                includeSeconds: false,
                dateStyle: 'medium',
                timeStyle: 'short',
                timeZoneName: null,
            }),
        []
    );

    const getStatusBadgeClass = useCallback((status) => {
        const normalized = (status || '').toLowerCase();
        if (normalized === 'completed') return 'quiz-card__status--completed';
        if (normalized === 'in_progress' || normalized === 'in progress') return 'quiz-card__status--in-progress';
        if (normalized === 'pending' || normalized === 'queued') return 'quiz-card__status--pending';
        if (normalized === 'failed' || normalized === 'error') return 'quiz-card__status--error';
        return 'quiz-card__status--default';
    }, []);

    const handleRefreshClick = useCallback(() => {
        fetchQuizzesAndInstances({ showSpinner: false });
    }, [fetchQuizzesAndInstances]);

    const confirmRegenerationCost = useCallback(({ estimatedPoints, numQuestions }) => {
        return new Promise((resolve) => {
            if (costConfirmationResolveRef.current) {
                costConfirmationResolveRef.current(false);
            }
            costConfirmationResolveRef.current = resolve;
            setCostConfirmation({ estimatedPoints, numQuestions });
        });
    }, []);

    const handleCostModalConfirm = useCallback(() => {
        setCostConfirmation(null);
        if (costConfirmationResolveRef.current) {
            costConfirmationResolveRef.current(true);
            costConfirmationResolveRef.current = null;
        }
    }, []);

    const handleCostModalCancel = useCallback(() => {
        setCostConfirmation(null);
        if (costConfirmationResolveRef.current) {
            costConfirmationResolveRef.current(false);
            costConfirmationResolveRef.current = null;
        }
    }, []);

    const handleRegenerateQuiz = useCallback(async () => {
        if (!document) {
            setError('No document selected.');
            return;
        }
        try {
            setRegenerateModalVisible(true);
            const { quizType, numQuestions } = await new Promise((resolve, reject) => {
                regeneratePromiseRef.current = { resolve, reject };
            });
            await regenerateQuiz({
                documentId: document.id,
                tokenCount: document.token_count,
                filename: document.filename,
                setApiStatus,
                startTimer,
                stopTimer,
                setLoading: handleRegenerationLoading,
                refreshDocuments: refreshAfterRegen,
                accessToken,
                supabaseRefreshToken,
                onSignIn,
                quizType,
                numQuestions,
                userId: document.user_id,
                confirmCost: confirmRegenerationCost,
            });
        } catch (error) {
            if (error.message !== 'User canceled') {
                console.error('[QuizList] Error regenerating quiz:', error);
                setApiStatus(error.message || 'An error occurred while regenerating the quiz.');
            }
        }
    }, [
        document,
        accessToken,
        supabaseRefreshToken,
        onSignIn,
        startTimer,
        stopTimer,
        handleRegenerationLoading,
        refreshAfterRegen,
        confirmRegenerationCost,
    ]);

    if (loading) {
        return (
            <div className="loading-container">
                <div className="spinner"></div>
                <p>Loading quizzes...</p>
            </div>
        );
    }

    if (error || !document) {
        return (
            <div className="error-container">
                <p>{error || 'No document selected.'}</p>
                <button className="button confirm-button" onClick={() => navigate('/')}>
                    Back to Home
                </button>
            </div>
        );
    }

    const statusSegments = [];
    if (apiStatus) {
        statusSegments.push(apiStatus);
    }
    if (refreshing) {
        statusSegments.push('Refreshing quiz list...');
    }
    const statusMessage = statusSegments.join(' | ') || (isRegenerating ? 'Working on your quiz...' : '');

    const hasQuizzes = Array.isArray(quizData) && quizData.length > 0;
    const hasInstances = sortedInstances.length > 0;

    return (
        <div className="scroll-container">
            <div className="document-list-wrapper quiz-list-wrapper">
                <div className="quiz-list-header">
                    <div className="quiz-list-header__titles">
                        <h2 className="sub-header">Quizzes for {document.filename}</h2>
                        <p className="quiz-list-header__subtitle">
                            Start a new attempt, check progress, or review your quiz results.
                        </p>
                    </div>
                </div>

                {statusMessage && (
                    <div className="api-status-container">
                        {(isRegenerating || refreshing) && <div className="spinner spinner--inline" aria-hidden="true"></div>}
                        <p className="api-status-text">{statusMessage}</p>
                    </div>
                )}

                <div className="search-container quiz-list-search">
                    <div className="search-input-wrapper">
                        <FaSearch className="search-icon" aria-hidden="true" />
                        <input
                            type="text"
                            className="search-input search-input--large"
                            placeholder="Search by quiz type, status, or instance ID..."
                            aria-label="Search quizzes"
                            value={searchTerm}
                            onChange={(event) => setSearchTerm(event.target.value)}
                        />
                        {searchTerm && (
                            <button
                                type="button"
                                className="clear-button"
                                onClick={() => setSearchTerm('')}
                                title="Clear search"
                                aria-label="Clear search"
                            >
                                <FaTimes className="clear-button-icon" aria-hidden="true" />
                            </button>
                        )}
                    </div>
                    <button
                        type="button"
                        className="button button--icon quiz-regenerate-button"
                        onClick={handleRegenerateQuiz}
                        disabled={loading || refreshing || isRegenerating}
                        title="Generate a fresh AI-powered quiz for this document"
                        aria-label="Regenerate new quiz"
                    >
                        <span className="quiz-regenerate-button__icon" aria-hidden="true">
                            <FaMagic />
                        </span>
                        <span className="quiz-regenerate-button__badge" aria-hidden="true">NEW</span>
                    </button>
                </div>

                <div className="document-list-container">
                    <div className="document-list-controls quiz-list-controls">
                        <div className="document-sort">
                            <span className="document-sort__label">Sort by</span>
                            <button
                                type="button"
                                className="document-sort-button"
                                onClick={() => handleSort('updated_at')}
                                aria-pressed={sortConfig.key === 'updated_at'}
                            >
                                Most Recent {getSortIcon('updated_at')}
                            </button>
                            <button
                                type="button"
                                className="document-sort-button"
                                onClick={() => handleSort('score')}
                                aria-pressed={sortConfig.key === 'score'}
                            >
                                Score {getSortIcon('score')}
                            </button>
                            <button
                                type="button"
                                className="document-sort-button"
                                onClick={() => handleSort('status')}
                                aria-pressed={sortConfig.key === 'status'}
                            >
                                Status {getSortIcon('status')}
                            </button>
                        </div>
                        <button
                            type="button"
                            className="button button--ghost quiz-refresh-button"
                            onClick={handleRefreshClick}
                            disabled={refreshing || loading || isRegenerating}
                        >
                            <FaSync aria-hidden="true" />
                            <span>{refreshing ? 'Refreshing...' : 'Refresh List'}</span>
                        </button>
                    </div>

                    {hasInstances ? (
                        <div className="document-grid quiz-grid">
                            {sortedInstances.map((instance, index) => {
                                const status = instance.status || 'Unknown';
                                const statusClass = getStatusBadgeClass(status);
                                const normalizedStatus = status.toLowerCase();
                                const isCompleted = normalizedStatus === 'completed';
                                const isInProgress = normalizedStatus === 'in progress' || normalizedStatus === 'in_progress';
                                const cardKey = instance.instance_id || `${instance.quizId}-${instance.updated_at || instance.created_at || index}`;

                                return (
                                    <div key={cardKey} className="document-card quiz-card">
                                        <div className="quiz-card__header">
                                            <div className="quiz-card__title-group">
                                                <h3 className="quiz-card__title">{instance.quizTitle}</h3>
                                                <span className={`quiz-card__status ${statusClass}`}>{status}</span>
                                            </div>
                                            <FaArrowRight aria-hidden="true" className="document-card__icon" />
                                        </div>
                                        <div className="quiz-card__meta">
                                            <span className="quiz-card__label">Instance ID</span>
                                            <span className="quiz-card__value">{instance.instance_id || 'N/A'}</span>
                                        </div>
                                        <div className="quiz-card__meta">
                                            <span className="quiz-card__label">Score</span>
                                            <span className="quiz-card__value">
                                                {instance.score_percentage !== null && instance.score_percentage !== undefined
                                                    ? `${instance.score_percentage}%`
                                                    : 'Pending'}
                                            </span>
                                        </div>
                                        <div className="quiz-card__meta">
                                            <span className="quiz-card__label">Updated</span>
                                            <span className="quiz-card__value">{formatDate(instance.updated_at)}</span>
                                        </div>
                                        <div className="quiz-card__actions">
                                            {isCompleted ? (
                                                <button
                                                    className="button quiz-card__button"
                                                    onClick={() => {
                                                        navigate('/results', {
                                                            state: {
                                                                selectedInstanceId: instance.instance_id,
                                                                selectedDocument: document,
                                                            },
                                                        });
                                                    }}
                                                >
                                                    View Results
                                                </button>
                                            ) : (
                                                <button
                                                    className="button quiz-card__button"
                                                    onClick={() => {
                                                        navigate('/quiz', {
                                                            state: {
                                                                selectedInstanceId: instance.instance_id,
                                                                selectedDocument: document,
                                                            },
                                                        });
                                                    }}
                                                >
                                                    {isInProgress ? 'Resume Quiz' : 'Take Quiz'}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="document-empty-state quiz-empty-state">
                            {searchTerm
                                ? 'No quiz instances match your search.'
                                : hasQuizzes
                                    ? 'No quiz instances available yet. Start a quiz to see it listed here.'
                                    : `No quizzes found for ${document.filename}. Use "Regenerate Quiz" to create a new quiz.`}
                        </div>
                    )}
                </div>

                <div className="quiz-list-footer">
                    <button className="button" onClick={() => navigate('/') }>
                        Back to Documents
                    </button>
                </div>
            </div>

            <QuizOptionsModal
                visible={regenerateModalVisible}
                onConfirm={(options) => {
                    setRegenerateModalVisible(false);
                    regeneratePromiseRef.current?.resolve(options);
                    regeneratePromiseRef.current = null;
                }}
                onCancel={() => {
                    setRegenerateModalVisible(false);
                    regeneratePromiseRef.current?.reject(new Error('User canceled'));
                    regeneratePromiseRef.current = null;
                }}
              />
              <PointsConfirmationModal
                  visible={Boolean(costConfirmation)}
                  estimatedPoints={costConfirmation?.estimatedPoints ?? 0}
                  numQuestions={costConfirmation?.numQuestions ?? 0}
                  onConfirm={handleCostModalConfirm}
                  onCancel={handleCostModalCancel}
                  title="Confirm quiz regeneration"
                  confirmText="Yes, regenerate"
            />
            {document && (
                <FloatingChat
                    documentId={document.id}
                    filename={document.filename}
                    accessToken={accessToken}
                />
            )}
        </div>
    );
};

export default QuizList;